"use client";

import * as React from "react";
import { ColumnDef } from "@tanstack/react-table";
import { Badge } from "@/components/ui/badge";
import { addMonths, endOfDay, format, startOfDay } from "date-fns";
import OrderDetails from "./OrderDetails";
import CommonList from "@/components/common/List";
import CommonTableList from "@/components/common/TableList";
import { ScrollArea, ScrollBar } from "@/components/ui/scroll-area";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import AssignDriver from "@/components/common/orders/AssignDriver";
import AssignStatus from "@/components/common/orders/AssignStatus";
import CompletionRequest from "@/components/common/orders/CompletionRequest";
import { handleRowClick, updateStatusByTotal } from "@/utils/functions";
import axiosInstance from "@/utils/axiosInstance";
import { tableCol } from "@/components/common/columns/columns";
import { Orders } from "@/interface/entityInterface";
import CustomTransfer from "../transfers/BookCustomTransfer";
import BookTour from "../tours/BookTour";
import { ResourceContextProvider } from "react-admin";
import { useLocation } from "react-router-dom";
import { DatePickerWithRange } from "@/components/custom/date-picker-with-range";
import { Input } from "@/components/ui/input";
import { DataTableFacetedFilterCustom } from "@/components/data-table/data-table-faceted-filter-custom";

export const OrdersList = () => {
  const { state } = useLocation();
  const [selectedOrder, setSelectedOrder] = React.useState<any | null>(null);
  const [isSheetOpen, setIsSheetOpen] = React.useState(false);

  const [selectedTab, setSelectedTab] = React.useState("ALL");
  const [selectedDate, setSelectedDate] = React.useState<Date>(new Date());
  const [statusMenu, setStatusMenu] = React.useState([]);
  const [totalOrders, setTotalOrders] = React.useState(0);
  const [endDate, setEndDate] = React.useState<Date>(addMonths(new Date(), 1));
  const [client, setClient] = React.useState("");
  const [drivers, setDrivers] = React.useState([]);
  const [orderStatusChanged, setStatusChanged] = React.useState<boolean>(false);
  const [sources, setSources] = React.useState<string[]>([]);

  React.useEffect(() => {
    if (!state) return;
    if (state.selectedDate && state.endDate) {
      setSelectedDate(state.selectedDate);
      setEndDate(state.endDate);
    }
  }, [state]);

  React.useEffect(() => {
    const unsub = async () => {
      const { data } = await axiosInstance.get("/drivers");
      setDrivers(data);
    };

    unsub();
  }, []);

  React.useEffect(() => {
    setStatusChanged(false);
    updateStatusByTotal(
      startOfDay(selectedDate),
      endOfDay(endDate),
      setStatusMenu,
      setTotalOrders,
      undefined,
      true,
    );
  }, [selectedDate, endDate, orderStatusChanged]);

  const columns: ColumnDef<Orders>[] = tableCol({
    showActions: false,
    columns: [
      {
        accessorKey: "commission_partner.name",
        header: "Order From",
        cell: ({ row }) => (
          <Badge
            variant="outline"
            className="px-1.5 text-muted-foreground capitalize"
          >
            {row.original.commission_partner?.name || row.original.source}
          </Badge>
        ),
      },
      {
        accessorKey: "created_at",
        header: "Created at",
        cell: ({ row }) => (
          <div className="capitalize">
            <p>
              {row.getValue("created_at")
                ? format(row.getValue("created_at"), "dd MMM y, HH:mm:ss")
                : "-"}
            </p>
          </div>
        ),
      },
      {
        accessorKey: "pickup",
        header: "Pickup",
        cell: ({ row }) => (
          <div className="capitalize">
            <p>{row.original.pickup}</p>
          </div>
        ),
      },
      {
        accessorKey: "dropoff",
        header: "Dropoff",
        cell: ({ row }) => {
          return (
            <div className="capitalize">
              <p>{row.original.dropoff}</p>
            </div>
          );
        },
      },
      {
        accessorKey: "client_name",
        header: "Client",
        cell: ({ row }) => <div>{row.getValue("client_name")}</div>,
      },
      {
        accessorKey: "order_type",
        header: "Type",
        cell: ({ row }) => (
          <div className="w-32">
            <Badge
              variant="outline"
              className="px-1.5 text-muted-foreground capitalize"
            >
              {row.original.order_type}
            </Badge>
          </div>
        ),
      },
      {
        accessorKey: "status",
        header: "Status",
        cell: ({ row }) => (
          <Badge
            variant="outline"
            className="flex gap-1 px-1.5 text-muted-foreground [&_svg]:size-3"
          >
            {row.original.status === "NEW_ORDER"
              ? "New Order"
              : row.original.status === "COMPLETED"
                ? "Completed"
                : row.original.status === "ACCEPTED"
                  ? "Approved"
                  : row.original.status === "REJECTED"
                    ? "Rejected"
                    : row.original.status === "CANCELLED"
                      ? "Cancelled"
                      : row.original.status === "ASSIGNED"
                        ? "Assigned"
                        : row.original.status === "COMPLETION_REQUEST"
                          ? "Completed Request"
                          : "In Progress"}
          </Badge>
        ),
      },
      ...(selectedTab !== "NEW_ORDER"
        ? [
            {
              accessorKey: "driver.name",
              header:
                selectedTab === "ACCEPTED"
                  ? "Assign to driver"
                  : "Driver assigned",
              cell: ({ row }: any) =>
                row.original.status === "ACCEPTED" ? (
                  <AssignDriver order={row.original} drivers={drivers} />
                ) : (
                  row.original.driver?.name || "-"
                ),
            },
          ]
        : []),
      ...(selectedTab == "NEW_ORDER"
        ? [
            {
              accessorKey: "accept_reject",
              header: "Accept or Reject",
              cell: ({ row }: any) => (
                <AssignStatus
                  id={row.original.id}
                  orderStatus={row.original.status}
                  setStatusChanged={setStatusChanged}
                />
              ),
            },
          ]
        : []),
      ...(selectedTab == "COMPLETION_REQUEST"
        ? [
            {
              accessorKey: "complete_order",
              header: "Complete Order",
              cell: ({ row }: any) => (
                <CompletionRequest id={row.original.id} />
              ),
            },
          ]
        : []),
      {
        accessorKey: "pickup_time",
        header: "Pickup Time",
        cell: ({ row }) => {
          return (
            <div className="capitalize">
              <p>
                {row.getValue("pickup_time")
                  ? format(row.getValue("pickup_time"), "dd MMM y, HH:mm:ss")
                  : ""}
              </p>
            </div>
          );
        },
      },
      {
        accessorKey: "fare",
        header: "Price",
        cell: ({ row }) => (
          <p>
            {row.getValue("fare")} {row.original.currency}
          </p>
        ),
      },
    ],
  });

  const renderToolbar = () => (
    <>
      <DatePickerWithRange
        title="Filter by pickup time"
        date={{ from: selectedDate, to: endDate }}
        setDate={(range) => {
          if (range?.from) setSelectedDate(range?.from);
          if (range?.to) setEndDate(range.to);
        }}
      />

      <Input
        placeholder={`Filter by client`}
        onChange={(e: React.FormEvent<HTMLInputElement>) =>
          setClient(e.currentTarget.value)
        }
        className="w-[200px] lg:w-[250px] h-8"
      />

      {sources && (
        <DataTableFacetedFilterCustom
          title="Source"
          selectedValues={
            sources && sources.length > 0 ? new Set(sources) : new Set<string>()
          }
          onChange={(selected: string[]) => {
            setSources(selected);
          }}
          options={[
            { label: "Dispatch", value: "DISPATCH" },
            { label: "Admin app", value: "ADMIN_APP" },
            { label: "Web booking", value: "WEB_BOOKING" },
            { label: "Partner", value: "PARTNER_PLATFORM" },
          ]}
        />
      )}
    </>
  );

  return (
    <ResourceContextProvider value="taxi-orders">
      <CommonList
        filters={
          selectedTab !== "ALL"
            ? {
                status: selectedTab,
                createdDateFrom: selectedDate
                  ? startOfDay(selectedDate).toISOString()
                  : undefined,
                createdDateTo: endDate
                  ? endOfDay(endDate).toISOString()
                  : undefined,
                client,
                sources
              }
            : {
                createdDateFrom: selectedDate
                  ? startOfDay(selectedDate).toISOString()
                  : undefined,
                createdDateTo: endDate
                  ? endOfDay(endDate).toISOString()
                  : undefined,
                client,
                sources
              }
        }
      >
        <div className="flex flex-col gap-4 mb-4">
          <ScrollArea>
            <Tabs
              value={selectedTab}
              onValueChange={(status: string) => {
                setSelectedTab(status);
              }}
            >
              <TabsList className="@4xl/main:flex">
                <TabsTrigger value={"ALL"}>
                  ALL <span>({totalOrders})</span>
                </TabsTrigger>
                {statusMenu.map((item: any, index: number) => (
                  <TabsTrigger key={index} value={item.status}>
                    {item.label}
                    <span>({item.total})</span>
                  </TabsTrigger>
                ))}
              </TabsList>
            </Tabs>
            <ScrollBar orientation="horizontal" />
          </ScrollArea>
        </div>
        <CommonTableList
          columns={columns}
          filterSearch="client"
          rowClick={(row: Orders) => handleRowClick(row, setSelectedOrder, setIsSheetOpen)}
          create={<CustomTransfer title="Create transfer order" />}
          anotherButton={<BookTour data={null} title="Create tour order" />}
          useListContextFilter={true}
          isExportButton={true}
          renderToolbarFilters={renderToolbar}
        />
      </CommonList>
      <OrderDetails
        isSheetOpen={isSheetOpen}
        setIsSheetOpen={setIsSheetOpen}
        selectedOrder={selectedOrder}
        setStatusChanged={setStatusChanged}
      />
    </ResourceContextProvider>
  );
};

export default OrdersList;
